import React, { useEffect, useState, Fragment } from "react";
import { Row, Col, Card, Button, Modal, Form, Badge, Spinner } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const FREQUENCY_OPTIONS = ["Daily", "Weekly", "Monthly", "Yearly"];

const ExpenseTypeMaster = () => {
  const [list, setList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");

  const [form, setForm] = useState({
    name: "",
    category: "Recurring",
    recurringFrequency: "Monthly",
    description: "",
    status: "Active",
  });

  /* =========================
     FETCH EXPENSE TYPES
  ========================= */
  const fetchTypes = async () => {
    setLoading(true);
    try {
      const res = await api.get("/expense-types?includeInactive=true");
      if (res.data.success) {
        setList(res.data.data);
        setFiltered(res.data.data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  /* =========================
     SEARCH FILTER
  ========================= */
  useEffect(() => {
    if (!search.trim()) {
      setFiltered(list);
    } else {
      const s = search.toLowerCase();
      setFiltered(list.filter((i) => i.name.toLowerCase().includes(s)));
    }
  }, [search, list]);

  /* =========================
     MODAL HANDLERS
  ========================= */
  const openAdd = () => {
    setEditId(null);
    setForm({
      name: "",
      category: "Recurring",
      recurringFrequency: "Monthly",
      description: "",
      status: "Active",
    });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditId(item._id);
    setForm({
      name: item.name,
      category: item.category || "Recurring",
      recurringFrequency: item.recurringFrequency || "Monthly",
      description: item.description || "",
      status: item.status || "Active",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (!saving) setShowModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "category") {
      setForm((prev) => ({
        ...prev,
        category: value,
        recurringFrequency: value === "Recurring" ? (prev.recurringFrequency || "Monthly") : "",
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  /* =========================
     SAVE (CREATE / UPDATE)
  ========================= */
  const handleSave = async () => {
    if (!form.name.trim()) {
      return swal("Validation", "Expense type name is required", "warning");
    }

    try {
      setSaving(true);
      if (editId) {
        await api.patch(`/expense-types/${editId}`, form);
      } else {
        await api.post("/expense-types", form);
      }

      swal("Success", "Expense type saved successfully", "success");
      closeModal();
      fetchTypes();
    } catch (err) {
      swal(
        "Error",
        err.response?.data?.message || "Operation failed",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  /* =========================
     DELETE / DEACTIVATE
  ========================= */
  const handleDelete = (item) => {
    swal({
      title: `Delete "${item.name}"?`,
      text: "This action will permanently remove the expense type if it is not in use.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (ok) => {
      if (!ok) return;
      try {
        await api.delete(`/expense-types/${item._id}`);
        swal("Deleted", "Expense type removed successfully", "success");
        fetchTypes();
      } catch (err) {
        swal(
          "Error",
          err.response?.data?.message || "Cannot delete",
          "error"
        );
      }
    });
  };

  return (
    <Fragment>
      <PageTitle activeMenu="Expense Type Master" motherMenu="Masters" />

      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Card.Title>Expense Types</Card.Title>
              <Button onClick={openAdd}>
                <i className="fa fa-plus me-2"></i> Add Expense Type
              </Button>
            </Card.Header>

            <Card.Body>
              {/* SEARCH */}
              <div className="mb-3">
                <Form.Control
                  placeholder="Search expense type..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* LOADING */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <table className="table table-bordered table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Category & Frequency</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th width="170" className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((item) => (
                      <tr key={item._id}>
                        <td>
                          <strong>{item.name}</strong>
                        </td>
                        <td>
                          {item.category === "Recurring" || !item.category ? (
                            <span className="badge badge-primary bg-primary me-1">
                              <i className="fa fa-sync-alt me-1"></i> Recurring ({item.recurringFrequency || "Monthly"})
                            </span>
                          ) : (
                            <span className="badge badge-secondary bg-secondary">
                              Non-Recurring
                            </span>
                          )}
                        </td>
                        <td>{item.description || "—"}</td>
                        <td>
                          <Badge bg={item.status === "Active" ? "success" : "secondary"}>
                            {item.status}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <Button
                            size="sm"
                            variant="warning"
                            className="me-2"
                            onClick={() => openEdit(item)}
                          >
                            Edit
                          </Button>

                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleDelete(item)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {!loading && filtered.length === 0 && (
                <div className="text-center text-muted py-4">
                  No expense types found
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ================= MODAL ================= */}
      <Modal show={showModal} onHide={closeModal} backdrop={saving ? "static" : true} centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{editId ? "Edit Expense Type" : "Add Expense Type"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Expense Type Name *</Form.Label>
            <Form.Control
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="e.g. Office Rent, Electricity Bill, Tea/Snacks"
              disabled={saving}
              required
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  disabled={saving}
                >
                  <option value="Recurring">Recurring</option>
                  <option value="Non-Recurring">Non-Recurring / One-Time</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {form.category === "Recurring" && (
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Recurring Frequency *</Form.Label>
                  <Form.Select
                    name="recurringFrequency"
                    value={form.recurringFrequency}
                    onChange={handleChange}
                    disabled={saving}
                  >
                    {FREQUENCY_OPTIONS.map((freq) => (
                      <option key={freq} value={freq}>{freq}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              name="description"
              value={form.description}
              onChange={handleChange}
              as="textarea"
              rows={2}
              placeholder="Optional remarks or notes about this expense type"
              disabled={saving}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              name="status"
              value={form.status}
              onChange={handleChange}
              disabled={saving}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default ExpenseTypeMaster;
